import React, { useState, useEffect } from 'react';
import { useWallet } from '../contexts/WalletContext';
import { apiService } from '../services/api';
import { DonationPage } from '../types';
import DonationPageManager from './DonationPageManager';

const DashboardPage: React.FC = () => {
  const { connected, publicKey } = useWallet();
  const [pages, setPages] = useState<DonationPage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedPage, setSelectedPage] = useState<DonationPage | null>(null);

  useEffect(() => {
    if (connected && publicKey) {
      loadPages();
    }
  }, [connected, publicKey]);

  const loadPages = async () => {
    if (!publicKey) return;

    try {
      setLoading(true);
      const response = await apiService.getDonationPagesByWallet(publicKey.toString());
      
      if (response.success && response.data) {
        setPages(response.data);
      } else {
        setError('Failed to load donation pages');
      }
    } catch (err) {
      setError('Failed to load donation pages');
      console.error('Error loading pages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSelect = (page: DonationPage) => {
    setSelectedPage(page);
  };

  const handleBackToList = () => {
    setSelectedPage(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!connected) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-6">🔗</div>
          <h2 className="text-3xl font-bold mb-4">Connect Your Wallet</h2>
          <p className="text-xl text-gray-300 mb-8">
            Please connect your Phantom wallet to manage your donation pages
          </p>
        </div>
      </div>
    );
  }

  if (selectedPage) {
    return <DonationPageManager page={selectedPage} onBack={handleBackToList} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-xl text-gray-300">Loading your donation pages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-6">❌</div>
          <h2 className="text-3xl font-bold mb-4 text-red-400">Error</h2>
          <p className="text-xl text-gray-300 mb-8">{error}</p>
          <button
            onClick={loadPages}
            className="btn-primary btn-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="text-6xl mb-6">📄</div>
          <h2 className="text-3xl font-bold mb-4">No Donation Pages Yet</h2>
          <p className="text-xl text-gray-300 mb-8">
            Create your first donation page to start receiving SOL donations from your viewers
          </p>
          <button
            onClick={() => window.location.href = '/create'}
            className="btn-primary btn-lg"
          >
            Create Your First Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ paddingTop: '48px', paddingBottom: '32px' }}>
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4">
            Your <span className="gradient-text">Donation Pages</span>
          </h1>
          <p className="text-xl text-gray-300">
            Manage your donation pages and track your SOL earnings
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="stat-card">
            <div className="stat-value">{pages.length}</div>
            <div className="stat-label">Total Pages</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">Active</div>
            <div className="stat-label">All Pages</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">SOL</div>
            <div className="stat-label">Ready to Receive</div>
          </div>
        </div>

        {/* Pages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pages.map((page) => (
            <div
              key={page.id}
              className="card cursor-pointer group"
              onClick={() => handlePageSelect(page)}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-white group-hover:text-purple-400 transition-colors">
                  {page.description || 'Donation Page'}
                </h3>
                <span className="text-sm text-gray-400">
                  {formatDate(page.created_at)}
                </span>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Min Amount:</span>
                  <span className="font-semibold text-purple-400">
                    {page.min_amount} SOL
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Page ID:</span>
                  <span className="font-mono text-xs text-gray-500">
                    {page.id.slice(0, 8)}...
                  </span>
                </div>
              </div>
              
              <div className="border-t border-gray-700 pt-4">
                <div style={{ display: 'flex', gap: '16px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(`${window.location.origin}/donate/${page.id}`);
                    }}
                    className="flex-1 btn-secondary btn-sm"
                  >
                    Copy Donation URL
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(`${window.location.origin}/obs/${page.id}`);
                    }}
                    className="flex-1 btn-primary btn-sm"
                  >
                    Copy OBS URL
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create New Page Button */}
        <div className="text-center" style={{ marginTop: '32px' }}>
          <button
            onClick={() => window.location.href = '/create'}
            className="btn-primary btn-lg"
          >
            + Create New Page
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;